import { AxiosRequestConfig } from 'axios';
import api from './api';

export interface PaginatedResult<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export class BaseService {
  protected endpoint: string;
  private static inflightRequests: Map<string, Promise<any>> = new Map();

  constructor(endpoint: string) {
    this.endpoint = endpoint;
  }

  private static makeRequestKey(method: string, url: string, data?: any): string {
    // Sort object keys before stringifying to ensure consistency
    const sortedData = data ? JSON.stringify(data, Object.keys(data).sort()) : '';
    return `${method}:${url}:${sortedData}`;
  }

  private request<T>(
    method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
    path: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<T> {
    const url = `${this.endpoint}${path}`;
    const key = BaseService.makeRequestKey(method, url, data);

    if (BaseService.inflightRequests.has(key)) {
      return BaseService.inflightRequests.get(key)!;
    }

    let req: Promise<T>;

    switch (method) {
      case 'GET':
        req = api.get(url, config).then(res => res.data);
        break;
      case 'POST':
        req = api.post(url, data, config).then(res => res.data);
        break;
      case 'PUT':
        req = api.put(url, data, config).then(res => res.data);
        break;
      case 'PATCH':
        req = api.patch(url, data, config).then(res => res.data);
        break;
      case 'DELETE':
        req = api.delete(url, config).then(res => res.data);
        break;
      default:
        throw new Error('Unsupported method');
    }

    const wrappedReq = req
      .then(result => {
        BaseService.inflightRequests.delete(key);
        return result;
      })
      .catch(error => {
        BaseService.inflightRequests.delete(key);
        throw error;
      });

    BaseService.inflightRequests.set(key, wrappedReq);
    return wrappedReq;
  }

  protected get<T>(path: string = '', config?: AxiosRequestConfig): Promise<T> {
    return this.request('GET', path, undefined, config);
  }

  protected post<T>(path: string = '', data?: any, config?: AxiosRequestConfig): Promise<T> {
    return this.request('POST', path, data, config);
  }

  protected put<T>(path: string = '', data?: any, config?: AxiosRequestConfig): Promise<T> {
    return this.request('PUT', path, data, config);
  }

  protected patch<T>(path: string = '', data?: any, config?: AxiosRequestConfig): Promise<T> {
    return this.request('PATCH', path, data, config);
  }

  protected delete<T>(path: string = '', config?: AxiosRequestConfig): Promise<T> {
    return this.request('DELETE', path, undefined, config);
  }
}

